import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery, useQueryClient } from 'react-query';
import clsx from 'clsx';
import PageHeader from '../../components/ui/PageHeader';
import UiCard from '../../components/ui/UiCard';
import FormPageLayout from '../../components/ui/FormPageLayout';
import FilterSelect from '../../components/ui/FilterSelect';
import { paginationItems, FORMA_LINK_BTN } from '../../utils/listPageUi';
import { spaEntityAPI } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { confirmDialog } from '../../utils/confirmBridge';
import notify from '../../utils/notify';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  BuildingLibraryIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ComputerDesktopIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const SPA_CLASSROOMS_KEY = ['spa', 'classrooms'];

/** DB stores canonical French labels; map to i18n for display-only. */
const CLASSROOM_STORED_TYPE_I18N_KEY = {
  'Salle de cours': 'classroomsPage.typeCourseRoom',
  'Laboratoire informatique': 'classroomsPage.typeComputerLab',
  'Salle de conférence': 'classroomsPage.typeConferenceRoom',
  'Atelier pratique': 'classroomsPage.typeWorkshop',
};

function classroomTypeDisplayLabel(storedType, t) {
  const path = CLASSROOM_STORED_TYPE_I18N_KEY[storedType];
  if (path) return t(path);
  return storedType?.trim() ? storedType : '—';
}

const ClassroomDetail = ({ classroom }) => {
  const { t } = useLanguage();
  if (!classroom) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
        <p className="text-slate-600 dark:text-slate-400">{t('classroomsPage.notFound')}</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-1">{classroom.name || t('classroomsPage.nameUndefined')}</h2>
          <p className="text-slate-600 dark:text-slate-400 text-sm">{t('classroomsPage.codeColon')} {classroom.code || t('classroomsPage.codeUndefined')}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1.5 rounded-full text-sm ${
            classroom.isAvailable 
            ? 'bg-green-500/10 text-green-400' 
            : 'bg-red-500/10 text-red-400'
          }`}>
            {classroom.isAvailable ? t('classroomsPage.available') : t('classroomsPage.occupied')}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <BuildingOfficeIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-2" />
            <span className="text-slate-700 dark:text-slate-300 text-sm">{t('classroomsPage.lblType')}</span>
          </div>
          <p className="text-slate-900 dark:text-slate-50">{classroomTypeDisplayLabel(classroom.type, t)}</p>
        </div>
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <UsersIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-2" />
            <span className="text-slate-700 dark:text-slate-300 text-sm">{t('classroomsPage.lblCapacityShort')}</span>
          </div>
          <p className="text-slate-900 dark:text-slate-50">{t('classroomsPage.capacitySuffix', { n: classroom.capacity || '0' })}</p>
        </div>
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <BuildingLibraryIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-2" />
            <span className="text-slate-700 dark:text-slate-300 text-sm">{t('classroomsPage.lblFloorShort')}</span>
          </div>
          <p className="text-slate-900 dark:text-slate-50">{classroom.floor || t('classroomsPage.notSpecifiedFloor')}</p>
        </div>
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <ComputerDesktopIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-2" />
            <span className="text-slate-700 dark:text-slate-300 text-sm">{t('classroomsPage.equipment')}</span>
          </div>
          <p className="text-slate-900 dark:text-slate-50">{Array.isArray(classroom.equipment) ? classroom.equipment.join(', ') : t('classroomsPage.noEquipment')}</p>
        </div>
      </div>

      {classroom.description && (
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4">{t('classroomsPage.descriptionHeading')}</h3>
          <p className="text-slate-700 dark:text-slate-300">{classroom.description}</p>
        </div>
      )}
      
      <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4">{t('classroomsPage.featuresHeading')}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="flex items-center">
            {classroom.hasProjector ? (
              <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
            ) : (
              <XCircleIcon className="h-5 w-5 text-red-400 mr-2" />
            )}
            <span className="text-slate-700 dark:text-slate-300">{t('classroomsPage.projector')}</span>
          </div>
          <div className="flex items-center">
            {classroom.hasAirConditioning ? (
              <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
            ) : (
              <XCircleIcon className="h-5 w-5 text-red-400 mr-2" />
            )}
            <span className="text-slate-700 dark:text-slate-300">{t('classroomsPage.airConditioning')}</span>
          </div>
          <div className="flex items-center">
            {classroom.hasComputers ? (
              <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
            ) : (
              <XCircleIcon className="h-5 w-5 text-red-400 mr-2" />
            )}
            <span className="text-slate-700 dark:text-slate-300">{t('classroomsPage.computers')}</span>
          </div>
          <div className="flex items-center">
            {classroom.hasWhiteboard ? (
              <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
            ) : (
              <XCircleIcon className="h-5 w-5 text-red-400 mr-2" />
            )}
            <span className="text-slate-700 dark:text-slate-300">{t('classroomsPage.whiteboard')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ClassroomForm = ({ isEditing = false, classroomData = null }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: classroomData?.name || "",
    code: classroomData?.code || `S${Math.floor(Math.random() * 100).toString().padStart(2, '0')}`,
    type: classroomData?.type || "",
    capacity: classroomData?.capacity || "",
    floor: classroomData?.floor || "",
    isAvailable: classroomData?.isAvailable === undefined ? true : classroomData.isAvailable,
    equipment: classroomData?.equipment || [],
    description: classroomData?.description || "",
    hasProjector: classroomData?.hasProjector || false,
    hasAirConditioning: classroomData?.hasAirConditioning || false,
    hasComputers: classroomData?.hasComputers || false,
    hasWhiteboard: classroomData?.hasWhiteboard || false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSave = {
        ...formData,
        equipment: typeof formData.equipment === 'string' ? 
                  formData.equipment.split(',').map(item => item.trim()).filter(Boolean) :
                  formData.equipment
      };

      if (isEditing && classroomData?.id) {
        await spaEntityAPI.update('classrooms', classroomData.id, { ...classroomData, ...dataToSave });
      } else {
        await spaEntityAPI.create('classrooms', dataToSave);
      }
      await queryClient.invalidateQueries(SPA_CLASSROOMS_KEY);
      navigate('/classrooms');
    } catch (error) {
      console.error('Error saving classroom data:', error);
      notify.show(t('classroomsPage.saveError'));
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
      <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-6">
        {isEditing ? t('classroomsPage.formTitleEdit') : t('classroomsPage.formTitleAdd')}
      </h2>
      
      <form className="space-y-6" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('classroomsPage.lblName')}
            </label>
                          <input 
              type="text" 
              name="name"
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('classroomsPage.placeholderName')}
              required
            />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('classroomsPage.lblCode')}
            </label>
                          <input 
              type="text" 
              name="code"
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              value={formData.code}
              onChange={handleChange}
              placeholder={t('classroomsPage.placeholderCode')}
              required
            />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('classroomsPage.lblTypeShort')}
            </label>
                          <select 
              name="type"
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              value={formData.type}
              onChange={handleChange}
              required
            >
              <option value="">{t('classroomsPage.selectType')}</option>
              <option value="Salle de cours">{t('classroomsPage.typeCourseRoom')}</option>
              <option value="Laboratoire informatique">{t('classroomsPage.typeComputerLab')}</option>
              <option value="Salle de conférence">{t('classroomsPage.typeConferenceRoom')}</option>
              <option value="Atelier pratique">{t('classroomsPage.typeWorkshop')}</option>
            </select>
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('classroomsPage.lblCapacityShort')}
            </label>
                          <input 
              type="number" 
              name="capacity"
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              value={formData.capacity}
              onChange={handleChange}
              min="1"
              placeholder={t('classroomsPage.capacityPlaceholderStudents')}
              required
            />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('classroomsPage.lblFloor')}
            </label>
                          <input 
              type="text" 
              name="floor"
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              value={formData.floor}
              onChange={handleChange}
              placeholder={t('classroomsPage.floorPlaceholder')}
            />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('classroomsPage.lblStatusShort')}
            </label>
                          <select 
              name="isAvailable"
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              value={formData.isAvailable}
              onChange={handleChange}
            >
              <option value="true">{t('classroomsPage.statusAvailableSelect')}</option>
              <option value="false">{t('classroomsPage.statusOccupiedSelect')}</option>
            </select>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('classroomsPage.lblEquipmentCsv')}
            </label>
                          <input 
              type="text" 
              name="equipment"
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              value={Array.isArray(formData.equipment) ? formData.equipment.join(', ') : formData.equipment}
              onChange={handleChange}
              placeholder={t('classroomsPage.equipmentCsvPlaceholder')}
            />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('classroomsPage.lblDescriptionShort')}
            </label>
                          <textarea 
              rows="3"
              name="description"
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              value={formData.description}
              onChange={handleChange}
              placeholder={t('classroomsPage.descriptionPlaceholder')}
            ></textarea>
          </div>
          
          <div className="md:col-span-2">
            <h3 className="text-slate-700 dark:text-slate-300 text-sm font-medium mb-3">{t('classroomsPage.featuresFormHeading')}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  name="hasProjector"
                  className="w-4 h-4 rounded text-forma-600 focus:ring-forma-500"
                  checked={formData.hasProjector}
                  onChange={handleChange}
                />
                <span className="text-slate-700 dark:text-slate-300">{t('classroomsPage.projector')}</span>
              </label>
              
              <label className="flex items-center space-x-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  name="hasAirConditioning"
                  className="w-4 h-4 rounded text-forma-600 focus:ring-forma-500"
                  checked={formData.hasAirConditioning}
                  onChange={handleChange}
                />
                <span className="text-slate-700 dark:text-slate-300">{t('classroomsPage.airConditioning')}</span>
              </label>
              
              <label className="flex items-center space-x-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  name="hasComputers"
                  className="w-4 h-4 rounded text-forma-600 focus:ring-forma-500"
                  checked={formData.hasComputers}
                  onChange={handleChange}
                />
                <span className="text-slate-700 dark:text-slate-300">{t('classroomsPage.computers')}</span>
              </label>
              
              <label className="flex items-center space-x-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  name="hasWhiteboard"
                  className="w-4 h-4 rounded text-forma-600 focus:ring-forma-500"
                  checked={formData.hasWhiteboard}
                  onChange={handleChange}
                />
                <span className="text-slate-700 dark:text-slate-300">{t('classroomsPage.whiteboard')}</span>
              </label>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end space-x-4">
          <Link 
            to="/classrooms"
            className="px-4 py-2 bg-slate-100/80 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors rounded-lg text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50"
          >
            {t('classroomsPage.cancelBtn')}
          </Link>
          <button
            type="submit"
            className="px-4 py-2 bg-forma-600 hover:bg-forma-500 transition-colors rounded-lg text-white"
          >
            {isEditing ? t('classroomsPage.submitUpdate') : t('classroomsPage.submitAdd')}
          </button>
        </div>
      </form>
    </div>
  );
};

const ClassroomsPage = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [classroomsPerPage] = useState(10);
  const [availabilityFilter, setAvailabilityFilter] = useState('all');

  const { data: classrooms = [], refetch: refetchClassrooms } = useQuery(
    SPA_CLASSROOMS_KEY,
    () => spaEntityAPI.list('classrooms'),
    { staleTime: 10_000 }
  );

  useEffect(() => {
    refetchClassrooms();
  }, [location.pathname, refetchClassrooms]);

  // Récupérer tous les types de salles uniques
  const classroomTypes = [...new Set(classrooms.map(classroom => classroom.type || '').filter(Boolean))];

  const allClassrooms = classrooms;
  const filteredClassrooms = allClassrooms.filter(classroom => {
    const matchesSearch = (classroom.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (classroom.code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (classroom.type || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    if (typeFilter !== 'all' && classroom.type !== typeFilter) return false;
    
    if (availabilityFilter === 'available' && !classroom.isAvailable) return false;
    if (availabilityFilter === 'occupied' && classroom.isAvailable) return false;
    
    return true;
  });
  
  const paginatedClassrooms = filteredClassrooms.slice((currentPage - 1) * classroomsPerPage, currentPage * classroomsPerPage);
  const totalClassrooms = filteredClassrooms.length;
  const totalPages = Math.ceil(totalClassrooms / classroomsPerPage);

  const pageSliceLabel =
    totalClassrooms === 0
      ? '—'
      : `${Math.min((currentPage - 1) * classroomsPerPage + 1, totalClassrooms)}–${Math.min(
          currentPage * classroomsPerPage,
          totalClassrooms
        )}`;

  const pageNav = useMemo(
    () =>
      paginationItems(
        Math.min(Math.max(currentPage, 1), Math.max(totalPages, 1)),
        Math.max(totalPages, 1)
      ),
    [currentPage, totalPages]
  );

  const formaLinkBtn = FORMA_LINK_BTN;

  const statsStrip = useMemo(
    () => [
      {
        title: t('classroomsPage.statRoomsTitle'),
        value: classrooms.length.toString(),
        hint: t('classroomsPage.statRoomsHint'),
        icon: BuildingLibraryIcon,
        iconWrap:
          'bg-forma-100 text-forma-700 shadow-inner dark:bg-forma-950/50 dark:text-forma-400',
      },
      {
        title: t('classroomsPage.statAvailableTitle'),
        value: classrooms.filter((c) => c.isAvailable).length.toString(),
        hint: t('classroomsPage.statAvailableHint'),
        icon: CheckCircleIcon,
        iconWrap:
          'bg-emerald-100 text-emerald-800 shadow-inner dark:bg-emerald-950/45 dark:text-emerald-400',
      },
      {
        title: t('classroomsPage.statCapacityTitle'),
        value: `${classrooms.reduce((acc, c) => acc + (Number(c.capacity) || 0), 0)}`,
        hint: t('classroomsPage.statCapacityHint'),
        icon: UsersIcon,
        iconWrap:
          'bg-rose-100 text-rose-800 shadow-inner dark:bg-rose-950/40 dark:text-rose-400',
      },
    ],
    [t, classrooms]
  );

  const typeTabs = useMemo(
    () => [
      { id: 'all', name: t('classroomsPage.typeTabsAll') },
      ...classroomTypes.map((ty) => ({
        id: ty,
        name: classroomTypeDisplayLabel(ty, t),
      })),
    ],
    [classroomTypes, t]
  );

  const typeTabCounts = useMemo(() => {
    const base = allClassrooms.filter((c) => {
      if (availabilityFilter === 'available' && !c.isAvailable) return false;
      if (availabilityFilter === 'occupied' && c.isAvailable) return false;
      const q = searchTerm.trim().toLowerCase();
      if (!q) return true;
      return (
        (c.name || '').toLowerCase().includes(q) ||
        (c.code || '').toLowerCase().includes(q) ||
        (c.type || '').toLowerCase().includes(q)
      );
    });
    const counts = { all: base.length };
    classroomTypes.forEach((ty) => {
      counts[ty] = base.filter((c) => c.type === ty).length;
    });
    return counts;
  }, [allClassrooms, searchTerm, availabilityFilter, classroomTypes]);

  const availTabs = useMemo(
    () => [
      { id: 'all', name: t('classroomsPage.availTabsAll') },
      { id: 'available', name: t('classroomsPage.availChipAvailable') },
      { id: 'occupied', name: t('classroomsPage.availChipOccupied') },
    ],
    [t]
  );

  const availTabCounts = useMemo(() => {
    const base = allClassrooms.filter((c) => {
      if (typeFilter !== 'all' && c.type !== typeFilter) return false;
      const q = searchTerm.trim().toLowerCase();
      if (!q) return true;
      return (
        (c.name || '').toLowerCase().includes(q) ||
        (c.code || '').toLowerCase().includes(q) ||
        (c.type || '').toLowerCase().includes(q)
      );
    });
    return {
      all: base.length,
      available: base.filter((c) => c.isAvailable).length,
      occupied: base.filter((c) => !c.isAvailable).length,
    };
  }, [allClassrooms, searchTerm, typeFilter]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleTypeFilter = (type) => {
    setTypeFilter(type);
    setCurrentPage(1);
  };
  
  const handleAvailabilityFilter = (availability) => {
    setAvailabilityFilter(availability);
    setCurrentPage(1);
  };

  const handleDelete = async (classroomId) => {
    if (await confirmDialog(t('classroomsPage.confirmDelete'))) {
      try {
        await spaEntityAPI.delete('classrooms', classroomId);
        await queryClient.invalidateQueries(SPA_CLASSROOMS_KEY);
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        notify.show(t('classroomsPage.deleteError'));
      }
    }
  };

  return (
    <Routes>
      <Route index element={
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <PageHeader
              title={t('classroomsPageTitle')}
              description={t('pageDescriptions.classrooms')}
              actions={
                <Link to="/classrooms/add" className={formaLinkBtn}>
                  <PlusIcon className="h-5 w-5" />
                  <span>{t('classroomsPage.newRoomShortcut')}</span>
                </Link>
              }
            />

            <UiCard padding={false} className="overflow-hidden">
              <div className="border-b border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-slate-50/80 dark:border-slate-700 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
                <div className="grid gap-6 p-6 sm:grid-cols-3 sm:gap-5">
                  {statsStrip.map((stat, index) => (
                    <motion.div
                      key={stat.title}
                      layout
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ type: 'spring', stiffness: 380, damping: 28, delay: index * 0.06 }}
                      whileHover={{ y: -3 }}
                      className="group relative rounded-2xl border border-slate-200/80 bg-white/80 p-[1px] shadow-sm backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-950/55"
                    >
                      <div className="relative overflow-hidden rounded-[0.9375rem] bg-gradient-to-b from-white/90 to-white/35 p-4 dark:from-slate-950/95 dark:to-slate-950/65">
                        <div
                          aria-hidden
                          className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br from-forma-200/35 to-transparent opacity-70 blur-2xl transition group-hover:opacity-100 dark:from-forma-600/25"
                        />
                        <div className="relative flex gap-4">
                          <div
                            className={clsx(
                              'relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-2 ring-white/70 dark:ring-slate-800/80',
                              stat.iconWrap
                            )}
                          >
                            <stat.icon className="h-6 w-6" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                              {stat.title}
                            </p>
                            <p className="mt-0.5 text-3xl font-bold tabular-nums tracking-tight text-slate-900 dark:text-white">
                              {stat.value}
                            </p>
                            <p className="mt-1 border-t border-slate-100 pt-2 text-xs text-slate-500 dark:border-slate-700/70 dark:text-slate-400">
                              {stat.hint}
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="border-b border-slate-200 bg-white/40 px-5 py-4 dark:border-slate-700 dark:bg-slate-900/35">
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
                  <FilterSelect
                    id="classroom-type-filter"
                    label={t('classroomsPage.lblRoomTypeUpper')}
                    value={typeFilter}
                    onChange={(v) => {
                      handleTypeFilter(v);
                      setCurrentPage(1);
                    }}
                    options={typeTabs.map((tab) => ({
                      id: tab.id,
                      name: tab.name,
                      count: typeTabCounts[tab.id] ?? 0,
                    }))}
                    className="sm:min-w-[11rem]"
                  />
                  <FilterSelect
                    id="classroom-avail-filter"
                    label={t('classroomsPage.lblAvailUpper')}
                    value={availabilityFilter}
                    onChange={(v) => {
                      handleAvailabilityFilter(v);
                      setCurrentPage(1);
                    }}
                    options={availTabs.map((tab) => ({
                      id: tab.id,
                      name: tab.name,
                      count: availTabCounts[tab.id] ?? 0,
                    }))}
                    className="sm:min-w-[11rem]"
                  />
                  <div className="relative min-w-0 flex-1 sm:min-w-[14rem]">
                    <label htmlFor="classroom-search" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {t('classroomsPage.searchSr')}
                    </label>
                    <MagnifyingGlassIcon className="pointer-events-none absolute bottom-2.5 left-3 h-5 w-5 text-slate-400 ltr:left-3 rtl:right-3" />
                    <input
                      id="classroom-search"
                      type="search"
                      autoComplete="off"
                      placeholder={t('classroomsPage.searchPlaceholder')}
                      value={searchTerm}
                      onChange={handleSearch}
                      className={clsx(
                        'w-full rounded-xl border border-slate-200 bg-white py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-forma-500 focus:outline-none focus:ring-2 focus:ring-forma-500/25 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500 ltr:pl-10 rtl:pr-10',
                        searchTerm.trim() ? 'ltr:pr-10 rtl:pl-10' : 'ltr:pr-4 rtl:pl-4'
                      )}
                    />
                    {searchTerm.trim() !== '' ? (
                      <button
                        type="button"
                        aria-label={t('classroomsPage.clearSearchAria')}
                        onClick={() => {
                          setSearchTerm('');
                          setCurrentPage(1);
                        }}
                        className="absolute bottom-1.5 rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 ltr:right-2 rtl:left-2 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              {(searchTerm.trim() !== '' || typeFilter !== 'all' || availabilityFilter !== 'all') && (
                <div className="flex flex-wrap items-center gap-2 border-b border-forma-200/60 bg-forma-50/50 px-5 py-2.5 dark:border-forma-900/35 dark:bg-forma-950/25">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-forma-800 dark:text-forma-400">
                    {t('classroomsPage.filtersActiveLbl')}
                  </span>
                  {searchTerm.trim() !== '' ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                      « {searchTerm.trim()} »
                    </span>
                  ) : null}
                  {typeFilter !== 'all' ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                      {classroomTypeDisplayLabel(typeFilter, t)}
                    </span>
                  ) : null}
                  {availabilityFilter !== 'all' ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                      {availabilityFilter === 'available'
                        ? t('classroomsPage.availChipAvailable')
                        : t('classroomsPage.availChipOccupied')}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('');
                      setTypeFilter('all');
                      setAvailabilityFilter('all');
                      setCurrentPage(1);
                    }}
                    className="ml-auto rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    {t('classroomsPage.resetBtn')}
                  </button>
                </div>
              )}

              {paginatedClassrooms.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-5 px-6 py-16 text-center">
                  <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60">
                    <BuildingLibraryIcon className="h-10 w-10 text-forma-500/70 dark:text-forma-400/80" />
                  </div>
                  <div className="max-w-md space-y-2">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{t('classroomsPage.emptyTitle')}</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{t('classroomsPage.emptyBody')}</p>
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTerm('');
                        setTypeFilter('all');
                        setAvailabilityFilter('all');
                        setCurrentPage(1);
                      }}
                      className="mx-auto rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      {t('classroomsPage.clearFiltersSearch')}
                    </button>
                  </div>
                  <Link to="/classrooms/add" className={formaLinkBtn}>
                    <PlusIcon className="h-5 w-5" />
                    <span>{t('classroomsPage.addRoom')}</span>
                  </Link>
                </div>
              ) : (
                <>
                  <div className="border-b border-slate-200 bg-gradient-to-r from-white to-slate-50/95 px-6 py-4 dark:border-slate-700 dark:from-slate-900 dark:to-slate-950/90">
                    <h2 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">{t('classroomsPage.listHeading')}</h2>
                    <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-600 dark:text-slate-400">
                      <span>
                        <span className="font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                          {totalClassrooms}
                        </span>{' '}
                        résultat{totalClassrooms > 1 ? 's' : ''}
                      </span>
                      <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                        ·
                      </span>
                      <span>
                        plage{' '}
                        <span className="font-medium tabular-nums text-slate-800 dark:text-slate-200">
                          {pageSliceLabel}
                        </span>{' '}
                        sur <span className="font-medium tabular-nums">{totalClassrooms}</span>
                      </span>
                      <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                        ·
                      </span>
                      <span className="text-slate-500">{classroomsPerPage} par page</span>
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] border-collapse text-left">
                      <thead className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 text-[11px] font-bold uppercase tracking-wider text-slate-500 shadow-[0_1px_0_rgba(15,23,42,0.06)] backdrop-blur-md dark:border-slate-700 dark:bg-slate-900/95 dark:text-slate-400">
                        <tr>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            Salle
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            Type
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            Capacité
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            Étage
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            Statut
                          </th>
                          <th scope="col" className="px-4 py-3.5 text-right font-bold">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedClassrooms.map((classroom, rowIdx) => (
                          <motion.tr
                            key={classroom.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: Math.min(rowIdx * 0.03, 0.35) }}
                            className={clsx(
                              'border-b border-slate-100 transition-colors dark:border-slate-800/80',
                              rowIdx % 2 === 1 && 'bg-slate-50/45 dark:bg-slate-900/40',
                              'hover:bg-forma-50/50 dark:hover:bg-forma-950/20'
                            )}
                          >
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <div className="flex items-center">
                                <div className="mr-3 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-forma-100 dark:bg-forma-950/50">
                                  <BuildingLibraryIcon className="h-5 w-5 text-forma-600 dark:text-forma-400" />
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-slate-900 dark:text-slate-50">
                                    {classroom.name || 'Sans nom'}
                                  </div>
                                  <div className="text-xs text-slate-600 dark:text-slate-400">
                                    {classroom.code || '—'}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <span className="rounded-full bg-forma-100 px-2.5 py-1 text-xs font-medium text-forma-800 dark:bg-forma-950/45 dark:text-forma-400">
                                {classroom.type || '—'}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5 text-sm text-slate-700 dark:text-slate-300">
                              {classroom.capacity ?? '0'} places
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5 text-sm text-slate-700 dark:text-slate-300">
                              {classroom.floor || '—'}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <span
                                className={clsx(
                                  'rounded-full px-2.5 py-1 text-xs font-medium',
                                  classroom.isAvailable
                                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-400'
                                    : 'bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-400'
                                )}
                              >
                                {classroom.isAvailable ? 'Disponible' : 'Occupée'}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5 text-right text-sm">
                              <div className="flex justify-end gap-1.5">
                                <Link
                                  to={`/classrooms/${classroom.id}`}
                                  className="rounded-lg border border-slate-200 bg-white p-2 text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-forma-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-forma-400"
                                  title="Voir"
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </Link>
                                <Link
                                  to={`/classrooms/edit/${classroom.id}`}
                                  className="rounded-lg border border-slate-200 bg-white p-2 text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-amber-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800"
                                  title="Modifier"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </Link>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(classroom.id)}
                                  className="rounded-lg border border-slate-200 bg-white p-2 text-red-600 shadow-sm transition hover:bg-red-50 dark:border-slate-600 dark:bg-slate-900 dark:text-red-400 dark:hover:bg-red-950/30"
                                  title="Supprimer"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {totalClassrooms > classroomsPerPage && (
                    <div className="flex flex-col gap-4 border-t border-slate-200 px-6 py-4 dark:border-slate-600 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-center text-sm text-slate-600 dark:text-slate-400 sm:text-left">
                        Affichage{' '}
                        <span className="font-semibold tabular-nums text-slate-800 dark:text-slate-200">
                          {pageSliceLabel}
                        </span>{' '}
                        sur <span className="font-semibold tabular-nums">{totalClassrooms}</span>
                        {' · '}page <span className="tabular-nums">{currentPage}</span> sur{' '}
                        <span className="tabular-nums">{totalPages}</span>
                      </p>
                      <nav
                        className="flex flex-wrap items-center justify-center gap-1.5 sm:justify-end"
                        aria-label={t('classroomsPage.paginationAria')}
                      >
                        <button
                          type="button"
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          <ChevronLeftIcon className="h-4 w-4" aria-hidden />
                          Précédent
                        </button>
                        <div className="mx-1 flex flex-wrap items-center justify-center gap-1">
                          {pageNav.map((item, idx) =>
                            item.type === 'ellipsis' ? (
                              <span
                                key={'e-' + idx}
                                className="px-2 font-medium text-slate-400 dark:text-slate-500"
                                aria-hidden
                              >
                                …
                              </span>
                            ) : (
                              <button
                                key={item.page}
                                type="button"
                                onClick={() => setCurrentPage(item.page)}
                                className={clsx(
                                  'min-w-[2.5rem] rounded-lg px-2 py-1.5 text-sm font-semibold tabular-nums transition',
                                  item.page === currentPage
                                    ? 'bg-forma-600 text-white shadow-sm'
                                    : 'border border-transparent text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800'
                                )}
                              >
                                {item.page}
                              </button>
                            )
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          Suivant
                          <ChevronRightIcon className="h-4 w-4" aria-hidden />
                        </button>
                      </nav>
                    </div>
                  )}
                </>
              )}
            </UiCard>

          </motion.div>
        }
      />
      <Route
        path="add"
        element={
          <FormPageLayout backTo="/classrooms" backLabel={t('classroomsPage.backToList')}>
            <ClassroomForm />
          </FormPageLayout>
        }
      />
      <Route path="edit/:id" element={<EditClassroomRoute />} />
      <Route path=":id" element={<ViewClassroomRoute />} />
    </Routes>
  );
};

const EditClassroomRoute = () => {
  const { t } = useLanguage();
  const { id } = useParams();
  const { data: classroom, isLoading } = useQuery(
    [...SPA_CLASSROOMS_KEY, id],
    () => spaEntityAPI.getById('classrooms', id),
    { enabled: Boolean(id) }
  );

  if (isLoading || !classroom) {
    return <div className="text-slate-900 dark:text-slate-50">{t('studentsPage.loading')}</div>;
  }

  return (
    <FormPageLayout backTo="/classrooms" backLabel={t('classroomsPage.backToList')}>
      <ClassroomForm isEditing classroomData={classroom} />
    </FormPageLayout>
  );
};

const ViewClassroomRoute = () => {
  const { t } = useLanguage();
  const { id } = useParams();
  const { data: classroom, isLoading, isError } = useQuery(
    [...SPA_CLASSROOMS_KEY, id],
    () => spaEntityAPI.getById('classrooms', id),
    { enabled: Boolean(id) }
  );

  if (isLoading) {
    return <div className="text-slate-900 dark:text-slate-50">{t('studentsPage.loading')}</div>;
  }

  if (isError || !classroom) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-700 dark:text-slate-300">{t('classroomsPage.notFound')}</p>
      </div>
    );
  }

  return (
    <FormPageLayout backTo="/classrooms" backLabel={t('classroomsPage.backToList')}>
      <ClassroomDetail classroom={classroom} />
    </FormPageLayout>
  );
};

export default ClassroomsPage; 